import { Link, NavLink, Outlet } from 'react-router-dom';
import { useEffect, useState, useSyncExternalStore } from 'react';
import { useAuth, type AuthUser } from '../auth/AuthContext';
import { BrandLogo } from './BrandLogo';
import { NavIcon, navTabAccent, navTabGlow } from './NavIcons';
import { UserAvatar } from './UserAvatar';
import { WelcomeTour } from './WelcomeTour';
import { isModuleEnabled } from '../lib/trackingPrefs';
import { getWelcomeTourCompleted, setWelcomeTourCompleted } from '../lib/welcomeTour';
import { isCoachChatBusy, subscribeCoachChat } from '../lib/coachChatSession';
import { PageAffiliateStrip } from './PageAffiliateStrip';
import { PageBannerAd } from './PageBannerAd';
import { AffiliatePageProvider } from './AffiliatePageProvider';
import { appPath } from '../lib/appPaths';

const coreNav = [
  { to: appPath('/nutrition'), label: 'Nutrition', icon: 'nutrition' as const, end: false, module: 'nutrition' as const, center: false },
  { to: appPath('/workouts'), label: 'Train', icon: 'workouts' as const, end: false, module: 'workouts' as const, center: false },
  { to: appPath('/'), label: 'Home', icon: 'home' as const, end: true, module: null, center: true },
  { to: appPath('/coach'), label: 'Coach', icon: 'coach' as const, end: false, module: null, center: false },
  { to: appPath('/more'), label: 'More', icon: 'more' as const, end: false, module: null, center: false },
];

type NavItem = (typeof coreNav)[number];

function enabledNav(user: AuthUser | null) {
  return coreNav.filter((item) => !item.module || isModuleEnabled(user, item.module));
}

/** Keep Home in the geometric center of the bottom bar when optional modules are hidden. */
function mobileNavOrder(items: NavItem[]) {
  const home = items.find((item) => item.center);
  if (!home) return items;
  const sides = items.filter((item) => !item.center);
  const left = sides.slice(0, Math.ceil(sides.length / 2));
  const right = sides.slice(Math.ceil(sides.length / 2));
  return [...left, home, ...right];
}

export function AppShell() {
  const { user } = useAuth();
  const nav = enabledNav(user);
  const bottomNav = mobileNavOrder(nav);
  const [showWelcomeTour, setShowWelcomeTour] = useState(false);
  const coachThinking = useSyncExternalStore(
    subscribeCoachChat,
    () => isCoachChatBusy(),
    () => false,
  );

  useEffect(() => {
    if (!user?.onboardingCompleted || !user.id) return;
    if (!getWelcomeTourCompleted(user.id)) {
      setShowWelcomeTour(true);
    }
  }, [user?.id, user?.onboardingCompleted]);

  function completeWelcomeTour() {
    if (user?.id) setWelcomeTourCompleted(user.id);
    setShowWelcomeTour(false);
  }

  const navLinkClass = (isActive: boolean, mobile?: boolean) =>
    mobile
      ? `relative flex min-w-0 flex-1 flex-col items-center gap-0.5 rounded-2xl px-1 py-1.5 transition-all duration-200 ${
          isActive ? '' : 'text-[var(--text-muted)] active:opacity-80'
        }`
      : `rounded-xl px-2.5 py-2 text-sm transition-colors ${
          isActive
            ? 'bg-white/[0.08] font-semibold text-[var(--text-primary)] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.1)]'
            : 'text-[var(--text-muted)] hover:bg-white/[0.05] hover:text-[var(--text-secondary)]'
        }`;

  return (
    <div className="app-body native-shell flex min-h-[100dvh] min-w-0 flex-col overflow-x-hidden overscroll-none">
      <header
        className="sticky top-0 z-20 hidden border-b backdrop-blur-2xl backdrop-saturate-150 md:block"
        style={{
          backgroundColor: 'var(--header-bg)',
          borderColor: 'var(--nav-border)',
        }}
      >
        <div className="mx-auto flex max-w-6xl min-w-0 items-center gap-2 px-4 py-3">
          <Link to={appPath('/')} className="shrink-0 rounded-[22%] p-0.5 opacity-95 transition-opacity hover:opacity-100" aria-label="Home">
            <BrandLogo heightClass="h-8 w-8" />
          </Link>
          <nav className="flex min-w-0 flex-1 justify-center gap-0.5 lg:gap-1" aria-label="Main">
            {bottomNav.map(({ to, label, icon, end }) => (
              <NavLink key={to} to={to} end={end} className={({ isActive }) => navLinkClass(isActive)}>
                {({ isActive }) => (
                  <span className="flex items-center gap-1.5">
                    <NavIcon name={icon} filled={isActive} className="h-4 w-4" />
                    <span>{label}</span>
                  </span>
                )}
              </NavLink>
            ))}
          </nav>
          <UserAvatar className="ml-auto" />
        </div>
      </header>

      <main className="mx-auto w-full max-w-6xl min-w-0 flex-1 overflow-x-hidden px-0 py-0 pb-mobile-nav-clearance pt-0 md:px-6 md:py-6 md:pb-8 md:pt-0">
        <AffiliatePageProvider>
          <Outlet />
          <div className="px-4 md:px-0">
            <PageAffiliateStrip />
            <PageBannerAd />
          </div>
        </AffiliatePageProvider>
      </main>

      <nav
        className="premium-bottom-nav fixed inset-x-0 bottom-0 z-30 border-t px-1 pb-[max(0.25rem,env(safe-area-inset-bottom,0px))] pt-1 md:hidden"
        style={{ backgroundColor: 'rgba(7, 10, 18, 0.98)' }}
        aria-label="Main"
      >
        <div className="mx-auto flex max-w-lg items-end justify-around">
          {bottomNav.map(({ to, label, icon, end, center }) => (
            <NavLink key={to} to={to} end={end} className={({ isActive }) => navLinkClass(isActive, true)}>
              {({ isActive }) => {
                const accent = isActive ? (navTabAccent[icon] ?? 'text-accent') : 'text-[var(--text-muted)]';
                const glow = isActive ? (navTabGlow[icon] ?? '') : '';

                if (center) {
                  return (
                    <>
                      <span
                        className={`flex items-center justify-center rounded-2xl transition-all duration-200 ${
                          isActive
                            ? 'h-12 w-12 -translate-y-1 bg-gradient-to-br from-sky-400 to-blue-600 shadow-[0_8px_24px_rgba(56,189,248,0.45)] ring-2 ring-white/15'
                            : 'h-8 w-[3.25rem] bg-transparent'
                        }`}
                      >
                        <NavIcon
                          name={icon}
                          filled={isActive}
                          className={`transition-colors duration-200 ${
                            isActive
                              ? 'h-6 w-6 text-white drop-shadow-sm'
                              : `h-[22px] w-[22px] opacity-55 ${accent}`
                          }`}
                        />
                      </span>
                      <span
                        className={`max-w-full truncate text-[10px] leading-tight tracking-tight ${
                          isActive ? 'font-bold text-white' : 'font-medium text-[var(--text-muted)]'
                        }`}
                      >
                        {label}
                      </span>
                    </>
                  );
                }

                return (
                  <>
                    <span
                      className={`relative flex h-8 w-[3.25rem] items-center justify-center rounded-2xl transition-all duration-200 ${
                        isActive ? 'bg-white/[0.08] shadow-[0_2px_12px_rgba(0,0,0,0.25)]' : ''
                      }`}
                    >
                      <NavIcon
                        name={icon}
                        filled={isActive}
                        className={`h-[22px] w-[22px] transition-colors duration-200 ${accent} ${
                          isActive ? glow : 'opacity-70'
                        }`}
                      />
                      {icon === 'coach' && coachThinking ? (
                        <span className="absolute -right-0.5 -top-0.5 flex h-2.5 w-2.5">
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-violet-400 opacity-60" />
                          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-violet-400" />
                        </span>
                      ) : null}
                    </span>
                    <span
                      className={`max-w-full truncate text-[10px] leading-tight tracking-tight ${
                        isActive ? `font-bold ${accent}` : 'font-medium text-[var(--text-muted)]'
                      }`}
                    >
                      {label}
                    </span>
                  </>
                );
              }}
            </NavLink>
          ))}
        </div>
      </nav>

      {showWelcomeTour && user ? <WelcomeTour user={user} onComplete={completeWelcomeTour} /> : null}
    </div>
  );
}
